import { get, post, put, del } from './client'
import type { User } from '@/types'

export interface LoginParams {
  username: string
  password: string
  totp_code?: string
}

export interface LoginResponse {
  user: User
  token?: string
}

interface BackendUserPayload {
  id: number
  username: string
  display_name?: string
  displayName?: string
  email?: string
  role?: number
  status?: number
  quota?: number
  used_quota?: number
  usedQuota?: number
  request_count?: number
  requestCount?: number
  group?: string
  remark?: string
  token?: string
  wechat_id?: string
  github_id?: string
  discord_id?: string
  linuxdo_id?: string
  linux_do_id?: string
  oidc_id?: string
  telegram_id?: string
  aff_code?: string
  aff_count?: number
  aff_quota?: number
  aff_history_quota?: number
  inviter_id?: number
  sidebar_modules?: string
  setting?: string
}

const normalizeUser = (payload: BackendUserPayload): User => {
  return {
    id: payload.id,
    username: payload.username,
    displayName: payload.displayName || payload.display_name || payload.username,
    email: payload.email || '',
    role: payload.role ?? 0,
    status: payload.status ?? 1,
    quota: payload.quota ?? 0,
    usedQuota: payload.usedQuota ?? payload.used_quota ?? 0,
    requestCount: payload.requestCount ?? payload.request_count ?? 0,
    group: payload.group || '',
    remark: payload.remark,
    token: payload.token,
    wechatId: payload.wechat_id,
    githubId: payload.github_id,
    linuxdoId: payload.linuxdo_id ?? payload.linux_do_id,
    discordId: payload.discord_id,
    oidcId: payload.oidc_id,
    telegramId: payload.telegram_id,
    affCode: payload.aff_code,
    affCount: payload.aff_count,
    affQuota: payload.aff_quota,
    affHistoryQuota: payload.aff_history_quota,
    inviterId: payload.inviter_id,
    sidebarModules: payload.sidebar_modules,
    setting: payload.setting,
  }
}

export interface RegisterParams {
  username: string
  password: string
  password2?: string
  email?: string
  verification_code?: string
  aff_code?: string
  turnstile?: string
}

export interface UpdateUserParams {
  username?: string
  displayName?: string
  display_name?: string
  email?: string
  password?: string
  original_password?: string
  sidebar_modules?: string
  language?: string
  setting?: string
  [key: string]: unknown
}

export type NotifyType = 'email' | 'webhook' | 'bark' | 'gotify'

export interface UpdateUserSettingParams {
  notify_type: NotifyType
  quota_warning_threshold: number
  webhook_url?: string
  webhook_secret?: string
  notification_email?: string
  bark_url?: string
  gotify_url?: string
  gotify_token?: string
  gotify_priority?: number
  upstream_model_update_notify_enabled?: boolean
  accept_unset_model_ratio_model: boolean
  record_ip_log: boolean
}

// ==================== 认证 ====================

/**
 * 用户登录
 */
export async function loginApi(params: LoginParams): Promise<LoginResponse> {
  const data = await post<BackendUserPayload | { require_2fa: true }>('/api/user/login', params)
  if ('require_2fa' in (data as object)) {
    throw Object.assign(new Error('需要 2FA 验证'), { require2fa: true })
  }
  const payload = data as BackendUserPayload
  return {
    user: normalizeUser(payload),
    token: payload.token,
  }
}

export async function registerApi(params: RegisterParams): Promise<void> {
  return post('/api/user/register', params)
}

export async function logoutApi(): Promise<void> {
  // 避免 401 走 client 全局处理里对 /login 的整页跳转，与控制台退出后回门户 `navigate('/')` 冲突
  return get('/api/user/logout', undefined, { skipErrorHandler: true })
}

export async function getUserInfoApi(): Promise<User> {
  const data = await get<BackendUserPayload>('/api/user/self')
  return normalizeUser(data)
}

export async function updateUserInfoApi(params: UpdateUserParams): Promise<void> {
  return put('/api/user/self', params)
}

export async function deleteUserApi(): Promise<void> {
  return del('/api/user/self')
}

// ==================== 邮箱 / 重置密码 ====================

export async function sendEmailVerificationApi(email: string): Promise<void> {
  return get('/api/verification', { email })
}

export async function sendPasswordResetEmailApi(email: string): Promise<void> {
  return get('/api/reset_password', { email })
}

export async function confirmPasswordResetApi(
  email: string,
  token: string,
  password: string
): Promise<void> {
  return post('/api/user/reset', { email, token, password })
}

export async function bindEmailApi(email: string, code: string): Promise<void> {
  return post('/api/oauth/email/bind', { email, code })
}

export async function bindWeChatApi(code: string): Promise<void> {
  return post('/api/oauth/wechat/bind', { code })
}

// ==================== 用户设置 ====================

export async function updateUserSettingApi(params: UpdateUserSettingParams): Promise<void> {
  return put('/api/user/setting', params)
}

// ==================== 访问令牌 ====================

export async function generateAccessTokenApi(): Promise<{ access_token: string } | string> {
  return get('/api/user/token')
}

// ==================== 用户可用模型 / 分组 ====================

export async function getUserModelsApi(): Promise<string[]> {
  return get<string[]>('/api/user/models')
}

export async function getUserGroupsApi(): Promise<Record<string, { desc: string; ratio: number }>> {
  return get('/api/user/self/groups')
}

export async function getAllGroupsApi(): Promise<Record<string, { desc: string; ratio: number }>> {
  return get('/api/user/groups')
}

// ==================== 2FA ====================

export async function get2FAStatusApi(): Promise<{
  enabled: boolean
  locked?: boolean
  backup_codes_remaining?: number
  secret?: string
  qr_code?: string
  qr_code_data?: string
}> {
  return get('/api/user/2fa/status')
}

export async function setup2FAApi(): Promise<{
  secret: string
  qr_code?: string
  qr_code_data?: string
  backup_codes?: string[]
}> {
  return post('/api/user/2fa/setup')
}

export async function enable2FAApi(code: string): Promise<void> {
  return post('/api/user/2fa/enable', { code })
}

export async function disable2FAApi(code: string): Promise<void> {
  return post('/api/user/2fa/disable', { code })
}

// ==================== Passkey ====================

export async function getPasskeyStatusApi(): Promise<{ enabled: boolean; last_used_at?: number }> {
  return get('/api/user/passkey')
}

export async function deletePasskeyApi(): Promise<void> {
  return del('/api/user/passkey')
}

export async function passkeyRegisterBeginApi(): Promise<{ options: unknown }> {
  return post('/api/user/passkey/register/begin')
}

export async function passkeyRegisterFinishApi(payload: unknown): Promise<void> {
  return post('/api/user/passkey/register/finish', payload)
}

// ==================== 签到 ====================

export interface CheckinStats {
  checked_in_today: boolean
  total_checkins?: number
  total_quota?: number
  checkin_count?: number
  records?: Array<{ checkin_date: string; quota_awarded: number }>
}

export interface CheckinStatus {
  enabled: boolean
  min_quota: number
  max_quota: number
  stats: CheckinStats
}

export async function getCheckinStatusApi(month?: string): Promise<CheckinStatus> {
  return get('/api/user/checkin', month ? { month } : undefined)
}

export async function doCheckinApi(turnstile?: string): Promise<{
  quota_awarded: number
  checkin_date: string
}> {
  const url = turnstile
    ? `/api/user/checkin?turnstile=${encodeURIComponent(turnstile)}`
    : '/api/user/checkin'
  return post(url)
}

// ==================== 邀请 ====================

export async function getAffiliateInfoApi(): Promise<{
  aff_code: string
  aff_count: number
  aff_amount: number
  aff_history_amount: number
}> {
  return get('/api/user/aff')
}

export async function transferAffQuotaApi(quota: number): Promise<void> {
  return post('/api/user/aff_transfer', { quota })
}

// ==================== 管理员：用户管理 ====================

export interface AdminUser extends User {
  createdTime?: number
  requestCount?: number
}

export interface UsersListResponse {
  items: AdminUser[]
  total: number
  page: number
  page_size: number
}

export async function getUsersApi(params?: {
  p?: number
  page_size?: number
  group?: string
}): Promise<UsersListResponse> {
  const data = await get<UsersListResponse>('/api/user/', params)
  return data
}

export async function searchUsersApi(params: {
  keyword?: string
  group?: string
  p?: number
  page_size?: number
}): Promise<UsersListResponse> {
  return get<UsersListResponse>('/api/user/search', params)
}

export async function getUserDetailApi(id: number): Promise<User> {
  const data = await get<BackendUserPayload>(`/api/user/${id}`)
  return normalizeUser(data)
}

export async function createUserAdminApi(data: {
  username: string
  password: string
  display_name?: string
}): Promise<void> {
  return post('/api/user/', data)
}

export async function updateUserAdminApi(data: Partial<AdminUser> & { id: number }): Promise<void> {
  return put('/api/user/', data)
}

export async function deleteUserAdminApi(id: number): Promise<void> {
  return del(`/api/user/${id}`)
}

export async function manageUserApi(params: {
  username: string
  action: 'disable' | 'enable' | 'delete' | 'promote' | 'demote'
}): Promise<void> {
  return post('/api/user/manage', params)
}

export async function adminResetUser2FAApi(userId: number): Promise<void> {
  return del(`/api/user/${userId}/2fa`)
}

export async function adminResetUserPasskeyApi(userId: number): Promise<void> {
  return del(`/api/user/${userId}/reset_passkey`)
}

export async function adminResetUserPasswordApi(
  userId: number,
  password: string
): Promise<void> {
  return post(`/api/user/${userId}/reset_password`, { password })
}

export interface UserBindingInfo {
  github_id?: string
  wechat_id?: string
  linuxdo_id?: string
  discord_id?: string
  oidc_id?: string
  telegram_id?: string
  email?: string
  [k: string]: unknown
}

/**
 * 获取用户第三方绑定信息
 * GET /api/user/:id/bindings
 */
export async function getUserBindingsApi(userId: number): Promise<UserBindingInfo> {
  return get(`/api/user/${userId}/bindings`)
}

/**
 * 管理员解绑用户的第三方账号
 * DELETE /api/user/:id/bindings/:provider
 */
export async function adminUnbindUserApi(userId: number, provider: string): Promise<void> {
  return del(`/api/user/${userId}/bindings/${provider}`)
}

// ==================== 用户 OAuth 绑定 ====================

export type { UserOAuthBinding } from '@/types'

export async function getUserOAuthBindingsApi(): Promise<import('@/types').UserOAuthBinding[]> {
  return get('/api/user/oauth/bindings')
}

export async function unbindCustomOAuthApi(providerId: number): Promise<void> {
  return del(`/api/user/oauth/bindings/${providerId}`)
}
